<template>
    <div class="bo-cell-datetime">
        <date-picker
            v-if="editable"
            :id="id + '_body_text'"
            :value="displayValue"
            :editable="editable"
            confirm
            value-type="date"
            :type="valueType"
            :format="dateFormat"
            :confirmText="generalTranslations.apply ?? 'Apply'"
            :disabled-date="disabledDate"
            :disabled-time="disableTime"
            :disabled-calendar-changer="disabledCalendarChanger"
            :placeholder="placeholder"
            :lang="datePickerLocale"
            :style="textOnly ? { width: 'auto' } : {}"
            :clearable="!textOnly"
            @change="changeHandler"
            popup-class="fixed-datepicker"
        >
            <template
                v-slot:input
                v-if="textOnly"
            >
                <span class="cursor-pointer">{{ defaultDate }}</span>
            </template>
            <template
                v-slot:input
                v-if="textOnly"
            >
                <slot name="input">
                    <span
                        v-if="textOnly"
                        class="cursor-pointer"
                        >{{ defaultDate }}</span
                    >
                </slot>
            </template>
            <template
                v-slot:icon-calendar
                v-if="textOnly || useChevronIcon"
            >
                <i
                    v-if="useChevronIcon && !textOnly"
                    class="icon-arrowdropdown"
                ></i>
                <span v-else></span>
            </template>
        </date-picker>
        <span v-else-if="!displayValue && placeholder">{{ placeholder }}</span>
        <input
            v-else-if="displayValueAsTextbox"
            :id="id + '_body_text'"
            :value="defaultDate"
            disabled="disabled"
            class="form-control"
        />
        <span
            v-else
            class="bo-cell-datetime readonly"
            :id="id + '_body_text'"
            >{{ defaultDate }}</span
        >
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import moment from "moment";

export default {
    components: { DatePicker },

    model: {
        prop: "value",
        event: "change",
    },

    data() {
        return {
            generalTranslations: window.bo?.generalTranslations ?? {},
            datePickerLocale: {
                formatLocale: {
                    months: window.bo?.generalTranslations?.date_range_months ?? [
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                    ],
                    monthsShort: window.bo?.generalTranslations?.date_range_monthsShort ?? [
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec",
                    ],
                    weekdays: window.bo?.generalTranslations?.date_range_weekdays ?? [
                        "Sunday",
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                    ],
                    weekdaysShort: window.bo?.generalTranslations?.date_range_weekdaysShort ?? [
                        "Sun",
                        "Mon",
                        "Tue",
                        "Wed",
                        "Thu",
                        "Fri",
                        "Sat",
                    ],
                    weekdaysMin: window.bo?.generalTranslations?.date_range_weekdaysMin ?? [
                        "Su",
                        "Mo",
                        "Tu",
                        "We",
                        "Th",
                        "Fr",
                        "Sa",
                    ],
                },
            },
        };
    },

    props: {
        id: {
            type: String,
            default: "",
        },
        value: {
            type: Date | String,
            default: null,
        },
        editable: {
            type: Boolean,
            default: false,
        },
        format: {
            type: String,
            default: null,
        },
        momentLocale: {
            type: String,
            default: window.bo?.generalTranslations?.momentLocale ?? "en", // current locale
        },
        withTime: {
            type: Boolean,
            default: false,
        },
        // Are dates before today allowed
        past: {
            type: Boolean,
            default: true,
        },
        min: {
            type: Date | String,
        },
        max: {
            type: Date | String,
        },
        disableDateCallback: {
            type: Function,
        },
        disableTimeCallback: {
            type: Function,
        },
        placeholder: {
            type: String,
            default: "",
        },
        isEndDate: {
            type: Boolean,
            default: false,
        },
        getSameDateInAllTimezone: {
            type: Boolean,
            default: false,
        },
        fromDate: {
            type: String,
            default: null,
        },
        repeatUnits: {
            type: String,
            default: null,
        },
        repeatMultiplier: {
            type: Number,
            default: null,
        },
        displayValueAsTextbox: {
            type: Boolean,
            default: false,
        },
        textOnly: {
            type: Boolean,
            default: false,
        },
        useChevronIcon: {
            type: Boolean,
            default: false,
        },
        // Replace a value earlier than min with min
        clampToMin: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        defaultDate() {
            if (!this.value) {
                return this.placeholder;
            }
            // Use displayValue so read-only end dates show the picked day, not the stored next-day value
            const newDate = moment(this.displayValue).locale(this.momentLocale).format(this.dateFormat);
            return moment(newDate, this.dateFormat, this.momentLocale, true).isValid() ? newDate : "";
        },

        dateFormat() {
            if (this.format) return this.format;

            if (this.withTime) {
                return (
                    window.bo?.dateFormat?.concat(" ", window.bo?.timeFormat?.replace("mm", "mm:ss") ?? "hh:mm:ss A") ??
                    "DD MMM YYYY hh:mm:ss A"
                );
            } else {
                return window.bo?.dateFormat ?? "DD MMM YYYY";
            }
        },

        valueType() {
            return this.withTime ? "datetime" : "date";
        },

        displayValue() {
            if (!this.value) {
                return null;
            }
            let val = typeof this.value === "string" ? moment(this.value).toDate() : this.value;
            if (false == this.isEndDate) {
                return val;
            }
            return moment(val).subtract(1, "d").toDate();
        },
    },

    model: {
        prop: "value",
        event: "change",
    },

    watch: {
        value: {
            handler(newVal) {
                this.clampValueToMin(newVal);
            },
            immediate: true,
        },
        min() {
            this.clampValueToMin(this.value);
        },
    },

    methods: {
        clampValueToMin(value) {
            if (!this.clampToMin || !value || !this.disabledDate(new Date(value))) {
                return;
            }
            let validMin = this.min;
            if (typeof validMin === "number") {
                validMin = moment().add(validMin, "days").toDate();
            } else if (typeof validMin === "string") {
                validMin = new Date(moment.utc(validMin).local());
            }
            if (!validMin) {
                return;
            }
            if (!this.withTime) {
                validMin = moment(validMin).startOf("day").toDate();
            }
            if (!this.disabledDate(validMin)) {
                this.$emit("change", validMin);
            }
        },

        changeHandler(value) {
            if (this.getSameDateInAllTimezone == true) {
                value = new Date(moment(value).utcOffset(0, true).format());
            }

            if (false == this.isEndDate) {
                this.$emit("change", value);
            } else {
                this.$emit("change", moment(value).add(1, "d").toDate());
            }
        },

        disabledCalendarChanger(panelDate) {
            const minDate = moment().subtract(5, "year").startOf("year");
            const maxDate = moment().add(5, "year").endOf("month");

            const date = moment(panelDate);

            return date.isBefore(minDate) || date.isAfter(maxDate);
        },

        disabledDate(date) {
            let disable = false;

            if (this.fromDate !== null && this.fromDate !== "") {
                const from = moment(this.fromDate, "YYYY-MM-DD");
                const today = moment().startOf("day");
                const maxDate = moment().add(5, "year").endOf("month");
                const currentDate = moment(date, "YYYY-MM-DD");

                if (currentDate.isBefore(today) || currentDate.isBefore(from)) {
                    return true;
                }

                let nextDate = from.clone();
                const repeatedDates = [];

                // Check if `from` is the last day of its month
                const isFromLastDay = from.date() === from.clone().endOf("month").date();

                // Skip past dates
                while (nextDate.isBefore(today)) {
                    nextDate.add(this.repeatMultiplier, this.repeatUnits);
                    if (isFromLastDay && this.repeatUnits == "months") {
                        nextDate = nextDate.clone().endOf("month");
                    }
                }

                // Add future repeat dates
                while (nextDate.isSameOrBefore(maxDate)) {
                    repeatedDates.push(nextDate.format("YYYY-MM-DD"));
                    nextDate.add(this.repeatMultiplier, this.repeatUnits);
                    if (isFromLastDay && this.repeatUnits == "months") {
                        nextDate = nextDate.clone().endOf("month");
                    }
                }

                const isAllowed = repeatedDates.includes(currentDate.format("YYYY-MM-DD"));
                return !isAllowed;
            }

            if (!this.past) {
                disable = disable || date < new Date(new Date().setHours(0, 0, 0, 0));
            }

            if (this.min) {
                let minDate = this.min;
                if (typeof this.min === "string") {
                    minDate = new Date(moment.utc(this.min).local());
                } else if (typeof this.min === "number") {
                    minDate = moment().add(this.min, "days").toDate();
                }
                disable = disable || date < new Date(new Date(minDate).setHours(0, 0, 0, 0));
            }

            if (this.max) {
                let maxDate = this.max;
                if (typeof this.max === "string") {
                    maxDate = new Date(moment.utc(this.max).local());
                } else if (typeof this.max === "number") {
                    maxDate = moment().add(this.max, "days").toDate();
                }
                if (this.withTime) {
                    disable = disable || date > maxDate;
                } else {
                    disable = disable || date > new Date(maxDate.setHours(0, 0, 0, 0));
                }
            }

            if (this.disableDateCallback) {
                disable = disable || this.disableDateCallback(date);
            }

            return disable;
        },

        disableTime(date) {
            let disable = false;

            if (this.disableTimeCallback) {
                disable = disable || this.disableTimeCallback(date);
            }

            return disable;
        },
    },
};
</script>
